import { Fragment, useCallback, useMemo, useState } from 'react';

import {
  Button,
  MenuItem,
  MenuList,
  Paper,
  Popover,
  TextField,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import { CheckBox, CheckBoxOutlineBlank } from '@material-ui/icons';

import classNames from 'classnames';

import { CountryUtils } from '../../utils';

const FLAG_STACK_THRESHOLD = 3;
const MAX_FLAGS_DISPLAYED = 8;

const useStyles = makeStyles({
  button: {
    // @ts-expect-error - MUI makeStyles types don't support CSS custom properties
    fontWeight: 'var(--bui-font-weight-regular)',
    textTransform: 'none',
    minWidth: 'auto',
    padding: 0,
    height: 'auto',
    '&:hover, &:active, &:focus': {
      backgroundColor: 'transparent',
      opacity: 1,
    },
  },
  readOnly: {
    opacity: 1,
    cursor: 'default',
  },
  flagGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    marginRight: 'var(--bui-space-1)',
  },
  flag: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    lineHeight: 1,
    marginLeft: '-4px',
    '&:first-child': {
      marginLeft: 0,
    },
  },
  searchField: {
    padding: 'var(--bui-space-2)',
  },
  menuList: {
    maxHeight: 300,
    overflow: 'auto',
  },
  checkIcon: {
    marginRight: 'var(--bui-space-1)',
    fontSize: 18,
    color: 'var(--bui-fg-secondary)',
  },
});

const isValidCountryCode = (v: unknown): v is string =>
  typeof v === 'string' && CountryUtils.countryCodes.includes(v);

export interface CountryAutocompleteProps {
  value: string | string[] | null;
  onChange: (value: string | string[] | null) => void;
  multiple?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
}

export const CountryAutocomplete = ({
  value,
  onChange,
  multiple = false,
  disabled = false,
  readOnly = false,
}: CountryAutocompleteProps) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');

  const selectedValues = useMemo(() => {
    let values: unknown[];
    if (Array.isArray(value)) {
      values = value;
    } else if (value) {
      values = [value];
    } else {
      values = [];
    }
    return values.filter(isValidCountryCode);
  }, [value]);

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const filteredCodes = useMemo(() => {
    if (!search) return CountryUtils.countryCodes;
    const lower = search.toLowerCase();
    return CountryUtils.countryCodes.filter(code =>
      CountryUtils.getCountryLabel(code).toLowerCase().includes(lower),
    );
  }, [search]);

  const displayLabel = useMemo(() => {
    if (selectedValues.length === 0) {
      return 'Select...';
    }

    if (multiple && selectedValues.length > FLAG_STACK_THRESHOLD) {
      const flagsToShow = selectedValues.slice(0, MAX_FLAGS_DISPLAYED);
      return (
        <Fragment>
          <span className={classes.flagGroup}>
            {flagsToShow.map(code => (
              <span key={code} className={classes.flag}>
                {CountryUtils.getCountryFlag(code)}
              </span>
            ))}
          </span>
          {selectedValues.length} countries
        </Fragment>
      );
    }

    return selectedValues.map(CountryUtils.getCountryLabel).join(', ');
  }, [selectedValues, multiple, classes.flagGroup, classes.flag]);

  const handleSelect = useCallback(
    (code: string) => {
      if (multiple) {
        const next = selectedSet.has(code)
          ? selectedValues.filter(c => c !== code)
          : [...selectedValues, code];
        onChange(next);
      } else {
        onChange(code);
        setAnchorEl(null);
        setSearch('');
      }
    },
    [multiple, onChange, selectedSet, selectedValues],
  );

  const handleOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!disabled && !readOnly) {
        setAnchorEl(e.currentTarget);
      }
    },
    [disabled, readOnly],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSearch('');
  }, []);

  const tooltipText = selectedValues
    .map(CountryUtils.getCountryLabel)
    .join(', ');

  const triggerButton = (
    <Button
      size="small"
      className={classNames(classes.button, { [classes.readOnly]: readOnly })}
      disabled={disabled || readOnly}
      onClick={handleOpen}
    >
      {displayLabel}
    </Button>
  );

  if (readOnly) {
    return (
      <Tooltip title={tooltipText} placement="top" arrow>
        <span>{triggerButton}</span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipText} placement="top" arrow>
      <span>
        {triggerButton}
        <Popover
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Paper>
            <div className={classes.searchField}>
              <TextField
                placeholder="Search countries..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                autoFocus
                inputProps={{
                  'aria-label': 'Select country',
                }}
              />
            </div>
            <MenuList className={classes.menuList}>
              {filteredCodes.map(code => (
                <MenuItem key={code} onClick={() => handleSelect(code)}>
                  {multiple && (
                    <span className={classes.checkIcon}>
                      {selectedSet.has(code) ? (
                        <CheckBox fontSize="inherit" />
                      ) : (
                        <CheckBoxOutlineBlank fontSize="inherit" />
                      )}
                    </span>
                  )}
                  {CountryUtils.getCountryLabel(code)}
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </Popover>
      </span>
    </Tooltip>
  );
};
